import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useGetUserId } from "../../api/api-hooks";
import { FaEnvelope, FaUser, FaUsers } from "react-icons/fa";

const statusOptions = [
  { label: "Active", value: "Active" },
  { label: "Deactivate", value: "Deactivate" },
  { label: "Suspend", value: "suspend" },
];

const roleOptions = [
  { label: "Admin", value: "admin" },
  // { label: "Editor", value: "editor" },
  // { label: "Viewer", value: "viewer" },
  { label: "User", value: "user" },
];

const getValidationSchema = () => {
  return z.object({
    user_update: z.object({
      user_email: z.string().email("Invalid email format"),
      user_role: z.string().min(1, "Role is required"),
      status: z.string().min(1, "Status is required"),
    }),
  });
};

const getEditDefaultValues = () => ({
  user_update: {
    user_email: "",
    user_role: "user",
    status: "active",
  },
});

const getDefaultValues = (userDetail: any) => ({
  user_update: {
    user_email: userDetail?.user_email || "",
    user_role: userDetail?.user_role?.[0] || "user",
    status: userDetail?.status || "active",
  },
});

function EditUser() {
  const { userId } = useParams();
  const { data: userDetail, isLoading } = useGetUserId(userId);

  const methods = useForm({
    defaultValues: getEditDefaultValues(),
    resolver: zodResolver(getValidationSchema()),
  });

  useEffect(() => {
    if (userDetail) {
      methods.reset(getDefaultValues(userDetail));
    }
  }, [userDetail, methods]);

  return (
    <FormProvider {...methods}>
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-r from-[#f8f9fa] to-[#e9ecef] p-6">
        <div className="w-full max-w-xl bg-white shadow-md rounded-2xl p-8 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
            Edit User
          </h2>

          {isLoading ? (
            <p className="text-center text-gray-600">Loading user details...</p>
          ) : (
            <form
              onSubmit={methods.handleSubmit((data) => console.log(data))}
              className="space-y-6"
            >
              <div className="relative">
                <label className="block text-gray-700 font-medium mb-1">
                  Email
                </label>
                <div className="relative">
                  <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="email"
                    {...methods.register("user_update.user_email")}
                    className="w-full pl-10 border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 outline-none bg-gray-50"
                    placeholder="Enter user email"
                  />
                </div>
                <p className="text-red-500 text-xs mt-1">
                  {methods.formState.errors.user_update?.user_email?.message}
                </p>
              </div>

              <div className="relative">
                <label className="block text-gray-700 font-medium mb-1">
                  Status (Active or Suspend)
                </label>
                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <Controller
                    name="user_update.status"
                    control={methods.control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full pl-10 border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 outline-none bg-gray-50"
                      >
                        {statusOptions.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <p className="text-red-500 text-xs mt-1">
                  {methods.formState.errors.user_update?.status?.message}
                </p>
              </div>

              <div className="relative">
                <label className="block text-gray-700 font-medium mb-1">
                  Role (Admin or User)
                </label>
                <div className="relative">
                  <FaUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <Controller
                    name="user_update.user_role"
                    control={methods.control}
                    render={({ field }) => (
                      <select
                        {...field}
                        className="w-full pl-10 border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-300 outline-none bg-gray-50"
                      >
                        {roleOptions.map(({ label, value }) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
                <p className="text-red-500 text-xs mt-1">
                  {methods.formState.errors.user_update?.user_role?.message}
                </p>
              </div>

              <div className="flex justify-center">
                <button
                  type="submit"
                  className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-all duration-300 shadow-md"
                >
                  Save Changes
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </FormProvider>
  );
}

export default EditUser;
